/**
 * E03-F05 (TIER-2 fidelity) — pure (DOM-free) view model for the per-type
 * structured document READ/PREVIEW render. This is the model half of the legacy
 * `app.renderDocContentHtml` (project-docs.js ~L364-538): instead of building an
 * HTML string it returns a typed tree of nodes that `DocumentView.tsx` renders
 * as React (never `dangerouslySetInnerHTML` — every text node is React-escaped).
 *
 * Legacy parity, per doc type:
 *   - jd_doc              → Title + Alternatives, then About Company / About Role
 *                           / Responsibilities / Requirements / Company Offers, each
 *                           optionally trailed by a grey AI-comment block.
 *   - interview_questions → grouped Technical / Behavioral / Culture Fit /
 *                           Client-Specific (ordered), plus a flat `questions`
 *                           fallback.
 *   - inmails             → one card per variant: "Variant N (tone)" + Subject +
 *                           body.
 *   - sourcing_sheet      → ~24 label/value rows (arrays joined with ", "), with the
 *                           `brief_summary` rendered as a full-width block.
 *   - kickoff_sheet       → either the structured field layout (header + rows +
 *                           sections) OR, when the AI returned plain markdown
 *                           (`kickoff_sheet`/`text` with no `header`/`title`), the
 *                           parsed markdown body + the nested Sourcing Sheet + the
 *                           JD copy.
 *   - client_followup_email / internal_notification → Subject + summary/missing +
 *                           body (inline markdown).
 *
 * Inline emphasis (`**bold**` / `*italic*` / `__underline__`) reuses
 * {@link renderMarkdownInline} from docGenerationModel so the same safe-segment
 * parser drives both the editor and this view.
 */
import { renderMarkdownInline, type InlineSegment } from './docGenerationModel.js';
// PK-QA-36 — the Sourcing Sheet field spec was relocated to `@eer/shared`
// (`docRender.ts`) so the shared `.doc` serializer (`renderSourcingSheet`) can
// reach it without importing back into `packages/web`. Imported here for the
// local `buildSourcingSheet` use + re-exported below so this module's existing
// call sites / tests keep importing it from `./docViewModel.js` unchanged.
import {
  INTERVIEW_QUESTION_FIELDS,
  SOURCING_SHEET_FIELDS,
  fieldValueHtml,
  isBlankListItem,
  isEmptyDocValue,
  KICKOFF_JD_HEADING,
  KICKOFF_SOURCING_HEADING,
  looksLikeHtml,
  resolveDocFields,
  scrubStorageLinksDeep,
  structureJdText,
  valueItemsHtml,
  type SourcingFieldSpec,
} from '@eer/shared';

export { SOURCING_SHEET_FIELDS };
export type { SourcingFieldSpec };

// ---------------------------------------------------------------------------
// Node tree
// ---------------------------------------------------------------------------

/**
 * A run of inline text.
 *
 * `segments` is the legacy Markdown path — escaped React with emphasis, no raw
 * HTML. `raw` is the value the segments were parsed FROM, added for KS-07: since
 * the prompts now emit HTML, the renderer needs the original string to decide
 * whether to take the Markdown path or hand the value to `RichTextView`. Without
 * it the information is already lost by render time and a generated document shows
 * its tags literally — the exact defect KS-07 set out to remove, mirrored into the
 * read view. Both fields are always populated, so a consumer reading `segments`
 * still gets sensible text.
 */
export interface InlineText {
  readonly segments: InlineSegment[];
  /** The unparsed source string (Markdown or HTML). */
  readonly raw: string;
}

/** A titled document heading (h2/h3/h4 depending on level). */
export interface HeadingNode {
  readonly kind: 'heading';
  readonly level: 2 | 3 | 4;
  readonly text: string;
}

/** A page/section title with optional sub-line (JD main title + alternatives). */
export interface TitleNode {
  readonly kind: 'title';
  readonly text: string;
  /** Small grey sub-line, e.g. "Alternatives: A, B". */
  readonly subtitle?: string;
}

/** A prose paragraph block (inline markdown, preserves newlines). */
export interface ProseNode {
  readonly kind: 'prose';
  readonly lines: InlineText[];
}

/** A bullet (unordered) or numbered (ordered) list of inline-markdown items. */
export interface ListNode {
  readonly kind: 'list';
  readonly ordered: boolean;
  readonly items: InlineText[];
}

/** A grey read-only AI-comment block (JD `*_comments`). */
export interface CommentNode {
  readonly kind: 'comment';
  readonly text: string;
}

/**
 * A single label/value row (Sourcing Sheet, Kickoff Sheet fields).
 *
 * The value is inline text, not a bare string: a prompt told to wrap every
 * paragraph in `<p>` does that for a one-word field too, and a raw string would
 * put `<p>Tel Aviv</p>` on screen.
 */
export interface RowNode {
  readonly kind: 'row';
  readonly label: string;
  readonly value: InlineText;
  /**
   * KS3-07 — the field exists in the spec but the generator left it blank.
   *
   * The row is still rendered: an empty field and an absent section are different
   * facts, and collapsing the first into the second made the Sourcing Sheet look
   * like a document that was never meant to have those parts.
   */
  readonly empty?: boolean;
}

/** A full-width labelled block (Sourcing Sheet brief summary). */
export interface BlockNode {
  readonly kind: 'block';
  readonly label: string;
  readonly lines: InlineText[];
  /** KS3-07 — rendered as a heading with room under it (see {@link RowNode}). */
  readonly empty?: boolean;
}

/** A horizontal rule / divider. */
export interface DividerNode {
  readonly kind: 'divider';
}

/** An InMail variant card (title + optional subject + body). */
export interface CardNode {
  readonly kind: 'card';
  readonly title: string;
  readonly subject?: string;
  readonly body: InlineText[];
}

/** A highlighted callout box (missing-info / summary). */
export interface CalloutNode {
  readonly kind: 'callout';
  readonly tone: 'warning' | 'info';
  readonly title: string;
  /** Either free prose or a bullet list. */
  readonly text?: InlineText;
  readonly items?: InlineText[];
}

export type DocViewNode =
  | HeadingNode
  | TitleNode
  | ProseNode
  | ListNode
  | CommentNode
  | RowNode
  | BlockNode
  | DividerNode
  | CardNode
  | CalloutNode;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Content = Record<string, unknown>;

function inline(text: string): InlineText {
  return { segments: renderMarkdownInline(text), raw: String(text ?? '') };
}

/**
 * Split a value into display lines the way the legacy view did — EXCEPT when the
 * value is HTML.
 *
 * The newline split is what gave a Markdown paragraph its line breaks. Applied to
 * HTML it cuts markup apart: `<ul>\n<li>a</li>\n</ul>` becomes three fragments,
 * each an unbalanced snippet the sanitizer then has to guess at, and the list stops
 * being a list. HTML carries its own block structure, so it stays one line and
 * `RichTextView` renders it.
 */
function displayLines(value: unknown, split: (text: string) => string[]): InlineText[] {
  const text = String(value ?? '');
  if (looksLikeHtml(text)) return [inline(text)];
  return split(text).map(inline);
}

/** Coerce any content value to a display string (arrays join with ", "). */
function asString(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  return String(value);
}

/** True when a value is empty (null / '' / empty array). */
function isEmpty(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim().length === 0;
}

/** Prose block from a string: split on blank lines, keep single newlines. */
function proseNode(value: unknown): ProseNode | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  return { kind: 'prose', lines: displayLines(text, (t) => t.split('\n')) };
}

/** Coerce a list value to string items (InMail-style objects → their body/text). */
function listItems(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((x) => {
      if (typeof x === 'string') return x;
      const o = (x ?? {}) as { text?: string; body?: string };
      return o.text ?? o.body ?? JSON.stringify(x);
    })
    .filter((x) => !isBlankListItem(x)); // E03-F08-T06 — no empty bullets
}

/** The JD-style comment block (grey), joined with "; " like legacy. */
function commentNode(value: unknown): CommentNode | null {
  if (isEmpty(value)) return null;
  const items = Array.isArray(value) ? value.map(String) : [String(value)];
  return { kind: 'comment', text: items.join('; ') };
}

// ---------------------------------------------------------------------------
// Per-type builders
// ---------------------------------------------------------------------------

/** jd_doc — title + 5 sections, each optionally trailed by a comment block. */
function buildJd(c: Content): DocViewNode[] {
  const nodes: DocViewNode[] = [];
  const tb = (c['position_title_block'] as Content | undefined) ?? {};
  const altTitles = Array.isArray(tb['alternative_titles'])
    ? (tb['alternative_titles'] as unknown[]).map(String)
    : [];
  nodes.push({
    kind: 'title',
    text: asString(tb['main_title']) || 'Untitled',
    subtitle: altTitles.length ? `Alternatives: ${altTitles.join(', ')}` : undefined,
  });

  const push = (label: string, body: DocViewNode | null, comments: unknown): void => {
    if (!body) return;
    nodes.push({ kind: 'heading', level: 4, text: label });
    nodes.push(body);
    const cm = commentNode(comments);
    if (cm) nodes.push(cm);
  };

  if (!isEmpty(c['about_company']))
    push('About Company', proseNode(c['about_company']), c['about_company_comments']);
  if (!isEmpty(c['about_role']))
    push('About Role', proseNode(c['about_role']), c['about_role_comments']);
  if (Array.isArray(c['responsibilities']) && (c['responsibilities'] as unknown[]).length)
    push(
      'Responsibilities',
      { kind: 'list', ordered: false, items: listItems(c['responsibilities']).map(inline) },
      c['responsibilities_comments'],
    );
  if (Array.isArray(c['requirements']) && (c['requirements'] as unknown[]).length)
    push(
      'Requirements',
      { kind: 'list', ordered: false, items: listItems(c['requirements']).map(inline) },
      c['requirements_comments'],
    );
  if (!isEmpty(c['company_offers']))
    push('Company Offers', proseNode(c['company_offers']), c['company_offers_comments']);

  return nodes;
}

/**
 * interview_questions — блоки вопросов (нумерованные) + запасной плоский
 * `questions`.
 *
 * E12-F05-T02: имена блоков берутся из общей спецификации со псевдонимами, а не
 * из зашитого здесь списка из четырёх. Живая проверка: артефакты проектов 168 и
 * 201 показывали ПУСТУЮ карточку — все их вопросы лежат под именами, которых в
 * том списке не было. Плюс пункт вопроса теперь может быть объектом
 * (`{question, assesses, red_flags}`) — раньше такой пункт превращался в
 * `JSON.stringify`.
 */
function buildInterviewQuestions(c: Content): DocViewNode[] {
  const nodes: DocViewNode[] = [];
  for (const f of resolveDocFields(INTERVIEW_QUESTION_FIELDS, c, ['questions'])) {
    if (f.empty) continue;
    const items = valueItemsHtml(f.value);
    if (items.length === 0) continue;
    nodes.push({ kind: 'heading', level: 4, text: f.label });
    nodes.push({ kind: 'list', ordered: true, items: items.map(inline) });
  }
  const flat = listItems(c['questions']);
  if (flat.length) {
    nodes.push({ kind: 'list', ordered: true, items: flat.map(inline) });
  }
  return nodes;
}

/** inmails — one card per variant (Variant N (tone) + subject + body). */
function buildInmails(c: Content): DocViewNode[] {
  const variants = Array.isArray(c['variants']) ? (c['variants'] as unknown[]) : [];
  if (!variants.length) return [];
  return variants.map((v, i) => {
    const o = (v ?? {}) as { tone?: string; subject?: string; body?: string };
    const tone = o.tone ? ` (${o.tone})` : '';
    const card: CardNode = {
      kind: 'card',
      title: `Variant ${i + 1}${tone}`,
      subject: o.subject ? String(o.subject) : undefined,
      body: displayLines(o.body, (t) => t.split('\n')),
    };
    return card;
  });
}

/** sourcing_sheet — label/value rows + full-width brief-summary block. */
function buildSourcingSheet(c: Content): DocViewNode[] {
  // A payload with NO spec field filled is not an empty sheet — it is a sheet the
  // model returned in prose form (the shared `.doc` renderer makes the same call
  // via `hasStructured`). Showing the KS3-07 skeleton there would bury the only
  // text the document has under two dozen empty labels.
  const fields = resolveDocFields(SOURCING_SHEET_FIELDS, c, ['text', 'kickoff_sheet']);
  if (fields.every((f) => !f.known || f.empty)) {
    const prose = Object.keys(c)
      .filter((k) => !isEmpty(c[k]))
      .flatMap((k) => parseBlockMarkdown(asString(c[k])));
    if (prose.length > 0) return prose;
  }

  const nodes: DocViewNode[] = [];
  for (const f of fields) {
    // KS3-07 — an unfilled field is SHOWN, empty. Skipping it hid the fact that
    // the sheet has such a section at all, so nobody could tell «the generator
    // found nothing» from «this document does not cover that».
    if (f.empty) {
      nodes.push(
        f.block
          ? { kind: 'block', label: f.label, lines: [], empty: true }
          : { kind: 'row', label: f.label, value: inline(''), empty: true },
      );
      continue;
    }
    if (f.block) {
      // Legacy: split ". Capital" boundaries into separate lines.
      const lines = displayLines(f.value, (t) =>
        t
          .replace(/\. ([A-Z])/g, '.\n$1')
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l.length > 0),
      );
      nodes.push({ kind: 'block', label: f.label, lines });
      continue;
    }
    // E12-F05-T02 — значение рисуется по своей форме общим `fieldValueHtml`:
    // `asString` на `{priority_1_must: [...]}` давал `[object Object]`, поэтому
    // такие поля раньше вообще не значились в спецификации и пропадали.
    nodes.push({ kind: 'row', label: f.label, value: inline(fieldValueHtml(f.value)) });
  }
  return nodes;
}

/**
 * Unwrap a nested sourcing-sheet value: the AI sometimes returns
 * `{ sourcing_sheet: {...}, jd_doc: {...} }` inside the kickoff `sourcing_sheet`
 * slot (legacy L465). When the inner object has its own `sourcing_sheet` and no
 * `position_title`, drop one level.
 */
function unwrapSourcing(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'sourcing_sheet' in (value as Content) &&
    !('position_title' in (value as Content))
  ) {
    return (value as Content)['sourcing_sheet'];
  }
  return value;
}

// ---------------------------------------------------------------------------
// Block markdown parser (legacy _renderMarkdown) → node tree (DOM-free)
// ---------------------------------------------------------------------------

/**
 * Parse a block of markdown text into view nodes: `#`/`##`/`###` headings,
 * `---` dividers, `- ` bullet lists, `N. ` numbered items, blank-line paragraph
 * separation. Inline emphasis is deferred to {@link renderMarkdownInline}.
 */
export function parseBlockMarkdown(text: string): DocViewNode[] {
  // KS-07 — the same text may now arrive as HTML (generate_kickoff_sheet and the
  // other free-form prompts emit it). Running the Markdown line parser over HTML
  // finds no `#`/`- ` markers, so every tag would fall through to a prose line and
  // be displayed literally. HTML already has its block structure; hand it over whole.
  if (looksLikeHtml(text)) return [{ kind: 'prose', lines: [inline(String(text))] }];

  const nodes: DocViewNode[] = [];
  let bullets: string[] = [];
  let para: string[] = [];

  const flushBullets = (): void => {
    if (bullets.length) {
      nodes.push({ kind: 'list', ordered: false, items: bullets.map(inline) });
      bullets = [];
    }
  };
  const flushPara = (): void => {
    if (para.length) {
      nodes.push({ kind: 'prose', lines: para.map(inline) });
      para = [];
    }
  };

  for (const raw of String(text ?? '').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      flushPara();
      continue;
    }
    let m: RegExpMatchArray | null;
    if ((m = line.match(/^###\s+(.+)/))) {
      flushBullets();
      flushPara();
      nodes.push({ kind: 'heading', level: 4, text: m[1]! });
    } else if ((m = line.match(/^##\s+(.+)/))) {
      flushBullets();
      flushPara();
      nodes.push({ kind: 'heading', level: 3, text: m[1]! });
    } else if ((m = line.match(/^#\s+(.+)/))) {
      flushBullets();
      flushPara();
      nodes.push({ kind: 'heading', level: 2, text: m[1]! });
    } else if (line === '---') {
      flushBullets();
      flushPara();
      nodes.push({ kind: 'divider' });
    } else if ((m = line.match(/^-\s+(.+)/))) {
      flushPara();
      if (!isBlankListItem(m[1])) bullets.push(m[1]!); // E03-F08-T06
    } else if ((m = line.match(/^\d+\.\s+(.+)/))) {
      // Legacy renders numbered items as plain indented lines; keep them prose.
      flushBullets();
      para.push(line);
    } else {
      flushBullets();
      para.push(line);
    }
  }
  flushBullets();
  flushPara();
  return nodes;
}

/**
 * kickoff_sheet — two shapes:
 *   1. plain-markdown (`kickoff_sheet`/`text`, no `header`/`title`): parse the
 *      markdown body, then append the nested Sourcing Sheet + JD copy sections.
 *   2. structured fields: header + label/value rows + prose sections + nested
 *      Sourcing Sheet + JD copy.
 */
function buildKickoffSheet(raw: Content): DocViewNode[] {
  // E03-F08-T07 — storage addresses / signed links are never shown (sheets
  // written before the rule carried `name: https://…r2…` lines).
  const c = scrubStorageLinksDeep(raw);
  const nodes: DocViewNode[] = [];
  const hasPlain = (c['text'] || c['kickoff_sheet']) && !c['header'] && !c['title'];

  // E03-F08-T07 — kick-off → Sourcing Sheet → Original JD from client; both
  // appendix blocks are always present, an empty one says so.
  const appendNested = (): void => {
    const ss = unwrapSourcing(c['sourcing_sheet']);
    nodes.push({ kind: 'divider' });
    nodes.push({ kind: 'heading', level: 3, text: KICKOFF_SOURCING_HEADING });
    if (isEmptyDocValue(ss)) {
      const p = proseNode('_Sourcing Sheet has not been generated yet._');
      if (p) nodes.push(p);
    } else if (typeof ss === 'string') {
      const p = proseNode(ss);
      if (p) nodes.push(p);
    } else {
      nodes.push(...buildSourcingSheet(ss as Content));
    }
    nodes.push({ kind: 'divider' });
    nodes.push({ kind: 'heading', level: 3, text: KICKOFF_JD_HEADING });
    if (isEmptyDocValue(c['jd_copy'])) {
      const p = proseNode("_No JD text was found in the client's files or links._");
      if (p) nodes.push(p);
    } else {
      // E03-F08-T08 — paragraphs/bullets of the client's JD kept (and stored
      // run-on text with inline `●` repaired) — same as the export.
      const jd =
        typeof c['jd_copy'] === 'string'
          ? structureJdText(c['jd_copy'])
          : JSON.stringify(c['jd_copy'], null, 2);
      nodes.push(...parseBlockMarkdown(jd));
    }
  };

  if (hasPlain) {
    const body = String(c['kickoff_sheet'] ?? c['text'] ?? '');
    nodes.push(...parseBlockMarkdown(body));
    appendNested();
    return nodes;
  }

  // Structured layout.
  if (!isEmpty(c['header'])) nodes.push({ kind: 'heading', level: 2, text: asString(c['header']) });

  const row = (label: string, key: string): void => {
    if (!isEmpty(c[key])) nodes.push({ kind: 'row', label, value: inline(asString(c[key])) });
  };
  const proseSection = (label: string, key: string): void => {
    if (isEmpty(c[key])) return;
    nodes.push({ kind: 'heading', level: 4, text: label });
    const p = proseNode(c[key]);
    if (p) nodes.push(p);
  };

  row('Start Date', 'start_date');
  row('On Call', 'on_call');
  if (!isEmpty(c['contacts'])) {
    nodes.push({ kind: 'heading', level: 4, text: 'Contacts' });
    const p = proseNode(c['contacts']);
    if (p) nodes.push(p);
  }
  row('Company Website', 'company_website');
  row('Submission Email', 'submission_email');
  nodes.push({ kind: 'divider' });
  row('Title', 'title');
  row('Alternate Titles', 'alternate_titles');
  row('Location', 'location');
  row('Reporting To', 'reporting_to');
  row('Experience', 'experience_years');
  row('Relevant Companies', 'relevant_companies');
  row('DNS Companies', 'dns_companies');
  row('Degree', 'degree');
  row('Direct Reports', 'direct_reports');
  row('Territory', 'territory');
  row('MUST Requirements', 'must_requirements');
  row('Salary', 'salary');
  nodes.push({ kind: 'divider' });
  proseSection('Company Overview', 'company_overview');
  proseSection('Selling Points', 'selling_points');
  proseSection('Notes from Call', 'notes_from_call');
  appendNested();
  return nodes;
}

/** client_followup_email — Subject + body + optional missing-items callout. */
function buildFollowupEmail(c: Content): DocViewNode[] {
  const nodes: DocViewNode[] = [];
  if (!isEmpty(c['subject']))
    nodes.push({ kind: 'row', label: 'Subject', value: inline(asString(c['subject'])) });
  const body = proseNode(c['body']);
  if (body) nodes.push(body);
  const missing = c['missing_items'];
  if (Array.isArray(missing) && missing.length) {
    nodes.push({
      kind: 'callout',
      tone: 'warning',
      title: 'Missing information',
      items: missing.map((m) => inline(String(m))),
    });
  }
  return nodes;
}

/** internal_notification — Subject + summary callout + body. */
function buildInternalNotification(c: Content): DocViewNode[] {
  const nodes: DocViewNode[] = [];
  if (!isEmpty(c['subject']))
    nodes.push({ kind: 'row', label: 'Subject', value: inline(asString(c['subject'])) });
  if (!isEmpty(c['summary'])) {
    nodes.push({
      kind: 'callout',
      tone: 'info',
      title: 'Summary',
      text: inline(asString(c['summary'])),
    });
  }
  const body = proseNode(c['body']);
  if (body) nodes.push(body);
  return nodes;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Build the structured view-node tree for a document type + content (the model
 * behind {@link DocumentView}). Unknown types return a single prose node with a
 * pretty-printed JSON dump (legacy raw-JSON fallback), so a view is never empty.
 */
export function buildDocView(docType: string, content: unknown): DocViewNode[] {
  const c = (content && typeof content === 'object' ? content : {}) as Content;
  switch (docType) {
    case 'jd_doc':
      return buildJd(c);
    case 'interview_questions':
      return buildInterviewQuestions(c);
    case 'inmails':
      return buildInmails(c);
    case 'sourcing_sheet':
      return buildSourcingSheet(c);
    case 'kickoff_sheet':
      return buildKickoffSheet(c);
    case 'client_followup_email':
      return buildFollowupEmail(c);
    case 'internal_notification':
      return buildInternalNotification(c);
    default:
      return [{ kind: 'prose', lines: [inline(JSON.stringify(content, null, 2))] }];
  }
}
